import {
  ArrowDownOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons';
import { Pie } from '@ant-design/plots';
import { Card, Spin, Typography } from 'antd';
import { FC, ReactNode, useMemo, useState } from 'react';

import { CHART_COLORS } from '../../../constants/styles';
import { DiscardReasonCount } from '../../../types/phlebotomist-dashboard';
import { DeltaIndicator } from '../../charts/DeltaIndicator';
import { PhlebTooltip } from '../../PhlebTooltip';

const { Text } = Typography;

// Human-readable labels for discard reasons
const REASON_LABELS: Record<string, string> = {
  Clots: 'Clots',
  LowVolume: 'Low Volume',
  Hemolyzed: 'Hemolyzed',
  SealIssue: 'Seal Issue',
  BrokenBag: 'Broken Bag',
  RedCellContamination: 'Red Cell Contamination',
  DonorHealthIssue: 'Donor Health Issue',
  Lipemic: 'Lipemic',
  Other: 'Other',
  Timeout: 'Unsuccessful',
  Expired: 'Expired',
};

function getReasonColor(label: string): string {
  const reasonKey =
    Object.entries(REASON_LABELS).find(([, lbl]) => lbl === label)?.[0] ||
    label;
  return CHART_COLORS.discardReasons[reasonKey] || '#95a5a6';
}

interface IDonationFailureReasonsCardProps {
  completelyFailedDonations: number;
  completelyFailedDiscards: DiscardReasonCount[];
  partiallyFailedDonations: number;
  partiallyFailedDiscards: DiscardReasonCount[];
  attemptedDonations: number;
  loading: boolean;
  paneWidth: number;
  comparisonCompletelyFailedDonations?: number;
  comparisonCompletelyFailedDiscards?: DiscardReasonCount[];
  comparisonPartiallyFailedDonations?: number;
  comparisonPartiallyFailedDiscards?: DiscardReasonCount[];
  comparisonAttemptedDonations?: number;
  showDebug?: boolean;
  /** Previous period data for trend arrows (normal mode only) */
  previousPeriodData?: {
    completelyFailedDonations: number;
    completelyFailedDiscards: DiscardReasonCount[];
    partiallyFailedDonations: number;
    partiallyFailedDiscards: DiscardReasonCount[];
    attemptedDonations: number;
  };
}

/** A single failure section: pie chart + legend + count text */
const FailureSection: FC<{
  title: ReactNode;
  discards: DiscardReasonCount[];
  donationCount: number;
  attemptedDonations: number;
  comparisonDiscards?: DiscardReasonCount[];
  comparisonDonationCount?: number;
  comparisonAttemptedDonations?: number;
  isCompact: boolean;
  isInlineMode?: boolean;
  size?: 'large' | 'small';
  legendPosition?: 'right' | 'below';
  /** Previous period data for trend arrows only (no ring chart) */
  previousPeriodDiscards?: DiscardReasonCount[];
  previousPeriodDonationCount?: number;
  previousPeriodAttemptedDonations?: number;
}> = ({
  title,
  discards,
  donationCount,
  attemptedDonations,
  comparisonDiscards,
  comparisonDonationCount,
  comparisonAttemptedDonations,
  isCompact,
  isInlineMode = false,
  size = 'large',
  legendPosition = 'right',
  previousPeriodDiscards,
  previousPeriodDonationCount,
  previousPeriodAttemptedDonations,
}) => {
  const totalDiscarded = useMemo(
    () => discards.reduce((sum, dp) => sum + dp.count, 0),
    [discards]
  );

  const chartData = useMemo(
    () =>
      discards
        .filter((dp) => dp.count > 0)
        .map((dp) => ({
          type: REASON_LABELS[dp.reason] || dp.reason,
          value: dp.count,
        })),
    [discards]
  );

  const comparisonTotal = useMemo(
    () =>
      comparisonDiscards
        ? comparisonDiscards.reduce((sum, dp) => sum + dp.count, 0)
        : 0,
    [comparisonDiscards]
  );

  const comparisonChartData = useMemo(
    () =>
      comparisonDiscards
        ? comparisonDiscards
            .filter((dp) => dp.count > 0)
            .map((dp) => ({
              type: REASON_LABELS[dp.reason] || dp.reason,
              value: dp.count,
            }))
        : [],
    [comparisonDiscards]
  );

  // Previous period totals for trend arrows (when no inline comparison)
  const prevTotal = useMemo(
    () =>
      previousPeriodDiscards
        ? previousPeriodDiscards.reduce((sum, dp) => sum + dp.count, 0)
        : 0,
    [previousPeriodDiscards]
  );

  const prevChartData = useMemo(
    () =>
      previousPeriodDiscards
        ? previousPeriodDiscards
            .filter((dp) => dp.count > 0)
            .map((dp) => ({
              type: REASON_LABELS[dp.reason] || dp.reason,
              value: dp.count,
            }))
        : [],
    [previousPeriodDiscards]
  );

  // Effective trend data: use inline comparison if available, else previous period
  const trendDiscardTotal = comparisonDiscards ? comparisonTotal : prevTotal;
  const trendChartData = comparisonDiscards
    ? comparisonChartData
    : prevChartData;
  const hasTrendData = comparisonDiscards
    ? comparisonChartData.length > 0
    : prevChartData.length > 0;

  const colorDomain = useMemo(() => chartData.map((d) => d.type), [chartData]);
  const colorRange = useMemo(
    () => chartData.map((d) => getReasonColor(d.type)),
    [chartData]
  );

  const isSmall = size === 'small';
  // Breakout charts (small) are 20% larger in inline mode
  const inlineBoost = isSmall && isInlineMode ? 1.2 : 1;
  const ringSize = Math.round(
    (isSmall ? (isCompact ? 100 : 120) : isCompact ? 140 : 170) * inlineBoost
  );

  const config = useMemo(
    () => ({
      data: chartData,
      angleField: 'value',
      colorField: 'type',
      innerRadius: 0.75,
      radius: 1,
      legend: false as const,
      tooltip: { title: 'type' as const },
      label: false as const,
      statistic: { title: false as const, content: false as const },
      scale: { color: { domain: colorDomain, range: colorRange } },
    }),
    [chartData, colorDomain, colorRange]
  );

  const compConfig = useMemo(
    () => ({
      data: comparisonChartData,
      angleField: 'value',
      colorField: 'type',
      innerRadius: 0.7,
      radius: 1,
      legend: false as const,
      tooltip: false as const,
      label: false as const,
      statistic: { title: false as const, content: false as const },
      scale: { color: { domain: colorDomain, range: colorRange } },
    }),
    [comparisonChartData, colorDomain, colorRange]
  );

  const hasComparison = comparisonDiscards && comparisonChartData.length > 0;
  const innerRingSize = Math.round(ringSize * 0.75);

  if (totalDiscarded === 0 && donationCount === 0) {
    return (
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: isSmall ? 13 : 15 }}>
          {title}
        </Text>
        <div
          style={{
            textAlign: 'center',
            padding: 20,
            color: '#999',
          }}
        >
          <Text type="secondary">None in this period</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16, textAlign: 'center' }}>
      <Text strong style={{ fontSize: isSmall ? 13 : 15 }}>
        {title}
      </Text>

      {donationCount > 0 &&
        (() => {
          const currentPct =
            attemptedDonations > 0
              ? (donationCount / attemptedDonations) * 100
              : 0;
          // Use inline comparison if available, otherwise fall back to previous period
          const trendDonationCount =
            comparisonDonationCount ?? previousPeriodDonationCount;
          const trendAttempted =
            comparisonAttemptedDonations ?? previousPeriodAttemptedDonations;
          const compPct =
            trendAttempted && trendDonationCount !== undefined
              ? trendAttempted > 0
                ? (trendDonationCount / trendAttempted) * 100
                : 0
              : undefined;
          const pctDelta =
            compPct !== undefined ? currentPct - compPct : undefined;
          const baseFontSize = isSmall ? 11 : 12;

          return (
            <div style={{ marginTop: 4, textAlign: 'center' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                }}
              >
                <Text
                  style={{
                    fontSize: baseFontSize,
                    fontStyle: 'italic',
                    color: '#1565c0',
                    fontWeight: '600',
                  }}
                >
                  -- {donationCount} Out of {attemptedDonations} Donation
                  {attemptedDonations !== 1 ? 's' : ''} had failures. --
                </Text>
                {pctDelta !== undefined && (
                  <span
                    style={{
                      color:
                        Math.abs(pctDelta) < 1
                          ? '#2f3a45'
                          : pctDelta > 0
                          ? '#f5222d'
                          : '#52c41a',
                      fontSize: baseFontSize - 1,
                      fontWeight: 800,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {Math.abs(pctDelta) < 1 ? (
                      <ArrowRightOutlined style={{ marginRight: 2 }} />
                    ) : pctDelta > 0 ? (
                      <ArrowUpOutlined style={{ marginRight: 2 }} />
                    ) : (
                      <ArrowDownOutlined style={{ marginRight: 2 }} />
                    )}
                    {Math.abs(pctDelta) < 1
                      ? 'FLAT'
                      : pctDelta > 0
                      ? 'UP'
                      : 'DOWN'}
                  </span>
                )}
              </div>
              {comparisonDonationCount !== undefined &&
                comparisonAttemptedDonations !== undefined &&
                comparisonDonationCount > 0 && (
                  <Text
                    style={{
                      display: 'block',
                      fontSize: baseFontSize - 2,
                      fontStyle: 'italic',
                      color: '#4f6d7a',
                      fontWeight: '600',
                    }}
                  >
                    -- {comparisonDonationCount} Out of{' '}
                    {comparisonAttemptedDonations} Donation
                    {comparisonAttemptedDonations !== 1 ? 's' : ''} had
                    failures. --
                  </Text>
                )}
            </div>
          );
        })()}

      <div
        style={{
          display: 'flex',
          flexDirection: legendPosition === 'below' ? 'column' : 'row',
          alignItems: 'center',
          gap: isCompact ? 12 : 20,
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginTop: 8,
        }}
      >
        {/* Donut chart */}
        {chartData.length > 0 && (
          <div
            style={{
              position: 'relative',
              width: ringSize,
              height: ringSize,
              flexShrink: 0,
            }}
          >
            <Pie {...config} width={ringSize} height={ringSize} />
            {hasComparison && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: innerRingSize,
                  height: innerRingSize,
                }}
              >
                <Pie
                  {...compConfig}
                  width={innerRingSize}
                  height={innerRingSize}
                />
              </div>
            )}
            {/* Center content */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: isSmall
                    ? isCompact
                      ? 16
                      : 20
                    : isCompact
                    ? 20
                    : 26,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {totalDiscarded}
              </div>
              {hasTrendData && !hasComparison && (
                <div style={{ marginTop: -5 }}>
                  <DeltaIndicator
                    current={totalDiscarded}
                    comparison={trendDiscardTotal}
                    higherIsBetter={false}
                    showZero
                    size="small"
                  />
                </div>
              )}
              {hasComparison && (
                <div
                  style={{
                    fontSize: isSmall
                      ? isCompact
                        ? 10
                        : 11
                      : isCompact
                      ? 11
                      : 13,
                    color: '#626262',
                    marginTop: 2,
                  }}
                >
                  {comparisonTotal}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Legend table */}
        <table
          style={{
            borderCollapse: 'collapse',
            fontSize: isSmall ? 12 : 13,
            minWidth: isSmall ? 120 : 140,
          }}
        >
          <tbody>
            {chartData.map((item) => {
              const color = getReasonColor(item.type);
              const pct =
                totalDiscarded > 0
                  ? Math.round((item.value / totalDiscarded) * 100)
                  : 0;

              // Trend arrow: compare current % vs comparison/previous period % for this reason
              const trendValue = hasTrendData
                ? trendChartData.find((c) => c.type === item.type)?.value || 0
                : undefined;
              const trendPct =
                trendValue !== undefined && trendDiscardTotal > 0
                  ? Math.round((trendValue / trendDiscardTotal) * 100)
                  : undefined;
              const pctDelta =
                trendPct !== undefined ? pct - trendPct : undefined;

              return (
                <tr key={item.type}>
                  <td
                    style={{
                      padding: `${isSmall ? 1 : 2}px 4px ${
                        isSmall ? 1 : 2
                      }px 0`,
                    }}
                  >
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        backgroundColor: color,
                      }}
                    />
                  </td>
                  <td
                    style={{
                      padding: `${isSmall ? 1 : 2}px 8px ${
                        isSmall ? 1 : 2
                      }px 0`,
                    }}
                  >
                    {item.type}
                  </td>
                  <td
                    style={{
                      padding: `${isSmall ? 1 : 2}px 8px ${
                        isSmall ? 1 : 2
                      }px 0`,
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{item.value}</span>
                    <span style={{ color: '#999', marginLeft: 4 }}>
                      ({pct}%)
                    </span>
                  </td>
                  <td
                    style={{
                      padding: `${isSmall ? 1 : 2}px 0`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {pctDelta !== undefined && (
                      <span
                        style={{
                          color:
                            Math.abs(pctDelta) < 1
                              ? '#2f3a45'
                              : pctDelta > 0
                              ? '#f5222d'
                              : '#52c41a',
                          fontSize: isSmall ? 11 : 12,
                          fontWeight: 800,
                        }}
                      >
                        {Math.abs(pctDelta) < 1 ? (
                          <ArrowRightOutlined style={{ marginRight: 2 }} />
                        ) : pctDelta > 0 ? (
                          <ArrowUpOutlined style={{ marginRight: 2 }} />
                        ) : (
                          <ArrowDownOutlined style={{ marginRight: 2 }} />
                        )}
                        {Math.abs(pctDelta) < 1
                          ? 'FLAT'
                          : pctDelta > 0
                          ? 'UP'
                          : 'DOWN'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** Merge two discard arrays, summing counts for matching reasons */
function mergeDiscards(
  a: DiscardReasonCount[],
  b: DiscardReasonCount[]
): DiscardReasonCount[] {
  const map = new Map<string, number>();
  for (const d of a) map.set(d.reason, (map.get(d.reason) || 0) + d.count);
  for (const d of b) map.set(d.reason, (map.get(d.reason) || 0) + d.count);
  return Array.from(map.entries()).map(([reason, count]) => ({
    reason,
    count,
  }));
}

export const DonationFailureReasonsCard: FC<
  IDonationFailureReasonsCardProps
> = ({
  completelyFailedDonations,
  completelyFailedDiscards,
  partiallyFailedDonations,
  partiallyFailedDiscards,
  attemptedDonations,
  loading,
  paneWidth,
  comparisonCompletelyFailedDonations,
  comparisonCompletelyFailedDiscards,
  comparisonPartiallyFailedDonations,
  comparisonPartiallyFailedDiscards,
  comparisonAttemptedDonations,
  showDebug,
  previousPeriodData,
}) => {
  const isCompact = paneWidth < 500;
  const isInlineMode = comparisonCompletelyFailedDonations !== undefined;
  const [expanded, setExpanded] = useState(false);

  const hasNoData =
    completelyFailedDonations === 0 && partiallyFailedDonations === 0;

  // Combined totals for "Total Failed Donation Units"
  const totalFailedDonations =
    completelyFailedDonations + partiallyFailedDonations;
  const totalFailedDiscards = useMemo(
    () => mergeDiscards(completelyFailedDiscards, partiallyFailedDiscards),
    [completelyFailedDiscards, partiallyFailedDiscards]
  );

  const comparisonTotalFailedDonations =
    comparisonCompletelyFailedDonations !== undefined &&
    comparisonPartiallyFailedDonations !== undefined
      ? comparisonCompletelyFailedDonations + comparisonPartiallyFailedDonations
      : undefined;
  const comparisonTotalFailedDiscards = useMemo(
    () =>
      comparisonCompletelyFailedDiscards && comparisonPartiallyFailedDiscards
        ? mergeDiscards(
            comparisonCompletelyFailedDiscards,
            comparisonPartiallyFailedDiscards
          )
        : undefined,
    [comparisonCompletelyFailedDiscards, comparisonPartiallyFailedDiscards]
  );

  // Previous period totals for trend arrows
  const prevTotalFailedDonations = previousPeriodData
    ? previousPeriodData.completelyFailedDonations +
      previousPeriodData.partiallyFailedDonations
    : undefined;
  const prevTotalFailedDiscards = useMemo(
    () =>
      previousPeriodData
        ? mergeDiscards(
            previousPeriodData.completelyFailedDiscards,
            previousPeriodData.partiallyFailedDiscards
          )
        : undefined,
    [previousPeriodData]
  );

  return (
    <Card
      title="Donation Failure Reasons"
      style={{ height: '100%' }}
      headStyle={{ fontSize: 18 }}
      extra={
        !hasNoData && !loading ? (
          <span
            onClick={() => setExpanded(!expanded)}
            style={{
              fontSize: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              backgroundColor: expanded ? '#1e40af' : '#3b82f6',
              color: '#fff',
              padding: expanded ? '4px 10px 2px' : '3px 10px',
              borderRadius: 4,
              cursor: 'pointer',
              userSelect: 'none',
              boxShadow: expanded
                ? 'inset 0 3px 6px rgba(0,0,0,0.35)'
                : '0 2px 4px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)',
              transform: expanded ? 'translateY(1px)' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            {expanded ? 'Hide Breakdown' : 'Show Breakdown'}
          </span>
        ) : undefined
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : hasNoData ? (
        <div
          style={{
            textAlign: 'center',
            padding: 40,
            color: '#999',
          }}
        >
          <Text type="secondary">No failed donations in this period</Text>
        </div>
      ) : (
        <>
          {/* Top row: Total Failed - centered, larger */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <FailureSection
              title={
                <PhlebTooltip text="Count of product units that were discarded across all donations during the period. Includes both completely failed donations (all units discarded) and partially failed donations (some units discarded). The donut chart shows the breakdown by discard reason.">
                  Total Failed Donation Units
                </PhlebTooltip>
              }
              discards={totalFailedDiscards}
              donationCount={totalFailedDonations}
              attemptedDonations={attemptedDonations}
              comparisonDiscards={comparisonTotalFailedDiscards}
              comparisonDonationCount={comparisonTotalFailedDonations}
              comparisonAttemptedDonations={comparisonAttemptedDonations}
              isCompact={isCompact}
              size="large"
              legendPosition="right"
              previousPeriodDiscards={prevTotalFailedDiscards}
              previousPeriodDonationCount={prevTotalFailedDonations}
              previousPeriodAttemptedDonations={
                previousPeriodData?.attemptedDonations
              }
            />
          </div>

          {/* Bottom row: Complete + Partial side by side, smaller (toggle) */}
          {expanded && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: isCompact ? 16 : 24,
                flexWrap: 'wrap',
                marginTop: 8,
              }}
            >
              <div style={{ flex: 1, minWidth: 200, maxWidth: 350 }}>
                <FailureSection
                  title="Completely Failed"
                  discards={completelyFailedDiscards}
                  donationCount={completelyFailedDonations}
                  attemptedDonations={attemptedDonations}
                  comparisonDiscards={comparisonCompletelyFailedDiscards}
                  comparisonDonationCount={comparisonCompletelyFailedDonations}
                  comparisonAttemptedDonations={comparisonAttemptedDonations}
                  isCompact={isCompact}
                  isInlineMode={isInlineMode}
                  size="small"
                  legendPosition="below"
                  previousPeriodDiscards={
                    previousPeriodData?.completelyFailedDiscards
                  }
                  previousPeriodDonationCount={
                    previousPeriodData?.completelyFailedDonations
                  }
                  previousPeriodAttemptedDonations={
                    previousPeriodData?.attemptedDonations
                  }
                />
              </div>
              <div style={{ flex: 1, minWidth: 200, maxWidth: 350 }}>
                <FailureSection
                  title="Partially Failed"
                  discards={partiallyFailedDiscards}
                  donationCount={partiallyFailedDonations}
                  attemptedDonations={attemptedDonations}
                  comparisonDiscards={comparisonPartiallyFailedDiscards}
                  comparisonDonationCount={comparisonPartiallyFailedDonations}
                  comparisonAttemptedDonations={comparisonAttemptedDonations}
                  isCompact={isCompact}
                  isInlineMode={isInlineMode}
                  size="small"
                  legendPosition="below"
                  previousPeriodDiscards={
                    previousPeriodData?.partiallyFailedDiscards
                  }
                  previousPeriodDonationCount={
                    previousPeriodData?.partiallyFailedDonations
                  }
                  previousPeriodAttemptedDonations={
                    previousPeriodData?.attemptedDonations
                  }
                />
              </div>
            </div>
          )}

          {showDebug && (
            <div
              style={{
                marginTop: 16,
                padding: 8,
                backgroundColor: '#fff5f5',
                border: '1px solid #f31a25',
                borderRadius: 4,
                fontSize: 11,
                fontFamily: 'monospace',
                color: '#f31a25',
              }}
            >
              {[
                {
                  label: 'Total Failed',
                  discards: totalFailedDiscards,
                  count: totalFailedDonations,
                },
                {
                  label: 'Completely Failed',
                  discards: completelyFailedDiscards,
                  count: completelyFailedDonations,
                },
                {
                  label: 'Partially Failed',
                  discards: partiallyFailedDiscards,
                  count: partiallyFailedDonations,
                },
              ].map((section) => {
                const productsTotal = section.discards.reduce(
                  (sum, dp) => sum + dp.count,
                  0
                );
                return (
                  <div key={section.label} style={{ marginBottom: 6 }}>
                    <div style={{ fontWeight: 700 }}>
                      DEBUG: {section.label}
                    </div>
                    <div>Donation count: {section.count}</div>
                    <div>
                      Discarded products: {productsTotal} (products, not
                      donations)
                    </div>
                    {section.discards.map((dp) => (
                      <div key={dp.reason} style={{ paddingLeft: 8 }}>
                        {REASON_LABELS[dp.reason] || dp.reason}: {dp.count}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Card>
  );
};
